import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { NewsArticle as NewsArticleModel } from '../models/news-article.model';

// Define a type for any schema.org node
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Schema = Record<string, any>;

@Injectable({
  providedIn: 'root',
})
export class StructuredDataService implements OnDestroy {
  private renderer: Renderer2;
  private graph: Schema[] = [];
  private routerSubscription: Subscription;
  private readonly SCRIPT_ID = 'app-structured-data-graph';

  constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    private router: Router
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

    // IMPORTANT: Clear the graph on every successful navigation to prevent data from leaking between pages.
    this.routerSubscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.clearSchema();
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
  }

  /**
   * Clears the current schema graph. Called automatically on navigation.
   */
  public clearSchema() {
    this.graph = [];
  }

  /**
   * Adds one or more schema nodes to the current page's graph.
   * Prevents adding nodes with duplicate @id.
   * @param newNodes A single schema object or an array of schema objects.
   */
  public addNodes(...newNodes: Schema[]) {
    for (const newNode of newNodes) {
      if (newNode['@id']) {
        const exists = this.graph.some(existingNode => existingNode['@id'] === newNode['@id']);
        if (!exists) {
          this.graph.push(newNode);
        }
        // Optional: else { console.warn(`StructuredDataService: Node with @id ${newNode['@id']} already exists. Skipping.`); }
      } else {
        // Node without @id, just add it (most important nodes should have @id)
        this.graph.push(newNode);
      }
    }
  }

  /**
   * The final method to be called on a page component (e.g., in ngOnInit).
   * It takes all collected nodes, builds the @graph, and injects the single script.
   */
  public generateAndInjectSchema() {
    if (this.graph.length === 0) {
      this.removeSchema(); // Clean up if no schema was generated
      return;
    }

    const schema = {
      '@context': 'https://schema.org',
      '@graph': this.graph,
    };

    this.removeSchema(); // Remove the old script before adding the new one

    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'type', 'application/ld+json');
    this.renderer.setAttribute(script, 'id', this.SCRIPT_ID);
    this.renderer.setProperty(script, 'text', JSON.stringify(schema));
    this.renderer.appendChild(this.document.head, script);
  }

  /**
   * Removes the main structured data script from the document head.
   */
  private removeSchema() {
    const script = this.document.getElementById(this.SCRIPT_ID);
    if (script) {
      this.renderer.removeChild(this.document.head, script);
    }
  }

  // --- Schema Generation Methods ---
  // These methods don't inject anything; they just add nodes to the graph.

  /**
   * Adds the core Organization and WebSite nodes to the graph.
   * Should be called on every page.
   */
  public addBaseNodes() {
    const origin = environment.canonicalOrigin;

    const organizationNode: Schema = {
      '@type': 'NewsMediaOrganization',
      '@id': `${origin}/#organization`,
      name: 'Summerry',
      url: origin,
      logo: {
        '@type': 'ImageObject',
        url: `${origin}/assets/favicons/favicon-96x96.png`,
        width: 96,
        height: 96,
      },
      sameAs: ['https://x.com/summerryai'],
    };

    const websiteNode: Schema = {
      '@type': 'WebSite',
      '@id': `${origin}/#website`,
      url: origin,
      name: 'Summerry',
      description: 'Stay updated with the latest AI news and summaries, curated by AI for relevance and clarity.',
      publisher: {
        '@id': `${origin}/#organization`,
      },
    };

    this.addNodes(organizationNode, websiteNode);
  }

  /**
   * Adds a BreadcrumbList node to the graph.
   * @param items An array of breadcrumb items.
   */
  public addBreadcrumb(items: Array<{ name: string; item?: string }>) {
    if (!items || items.length === 0) return;

    // Use router.url to get the current path, cleaned of query params and hash
    const currentPath = this.router.url.split('?')[0].split('#')[0];
    // Use URL constructor for robust joining and normalization, preventing double slashes.
    const pageCanonicalUrl = new URL(currentPath, environment.canonicalOrigin).href;

    const itemListElement = items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.item && { item: item.item }),
    }));

    const breadcrumbNode: Schema = {
      '@type': 'BreadcrumbList',
      '@id': `${pageCanonicalUrl}#breadcrumb`, // Append #breadcrumb directly
      itemListElement,
    };

    this.addNodes(breadcrumbNode);
  }

  /**
   * Adds a rich NewsArticle node and its corresponding WebPage node to the graph.
   */
  public addNewsArticle(
    article: NewsArticleModel,
    previousArticle?: NewsArticleModel | null,
    nextArticle?: NewsArticleModel | null
  ) {
    const origin = environment.canonicalOrigin;
    const canonicalUrl = `${origin}/news/${article.slug}`;

    const articleNode: Schema = {
      '@type': 'NewsArticle',
      '@id': `${canonicalUrl}#article`,
      mainEntityOfPage: {
        '@id': canonicalUrl, // Correct: Points to the WebPage URL
      },
      headline: article.title,
      description: article.summary,
      image: {
        '@type': 'ImageObject',
        url: `${origin}/assets/og/black-og-image-1x.png`,
        width: 1200, // Recommended
        height: 630, // Recommended
      },
      datePublished: article.sources?.[0]?.timestamp || new Date().toISOString(),
      dateModified: new Date().toISOString(),
      author: {
        '@id': `${origin}/#organization`,
      },
      publisher: {
        '@id': `${origin}/#organization`,
      },
      keywords: article.keywords?.join(', '),
      // Integrate navigation directly
      ...(nextArticle && {
        successor: {
          '@type': 'NewsArticle',
          '@id': `${origin}/news/${nextArticle.slug}#article`,
          mainEntityOfPage: `${origin}/news/${nextArticle.slug}`,
          headline: nextArticle.title,
          url: `${origin}/news/${nextArticle.slug}`,
          author: {
            '@id': `${origin}/#organization`,
          },
          image: `${origin}/assets/og/black-og-image-1x.png`,
        },
      }),
      ...(previousArticle && {
        predecessor: {
          '@type': 'NewsArticle',
          '@id': `${origin}/news/${previousArticle.slug}#article`,
          mainEntityOfPage: `${origin}/news/${previousArticle.slug}`,
          headline: previousArticle.title,
          url: `${origin}/news/${previousArticle.slug}`,
          author: {
            '@id': `${origin}/#organization`,
          },
          image: {
            '@type': 'ImageObject',
            url: `${origin}/assets/og/black-og-image-1x.png`,
            width: 1200,
            height: 630,
          },
        },
      }),
    };

    const webpageNode: Schema = {
      '@type': 'WebPage',
      '@id': canonicalUrl,
      url: canonicalUrl,
      name: `${article.title} | Summerry`,
      isPartOf: {
        '@id': `${origin}/#website`,
      },
      breadcrumb: {
        '@id': `${canonicalUrl}#breadcrumb`, // Corrected: Removed trailing slash before #breadcrumb
      },
      potentialAction: {
        '@type': 'ReadAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: canonicalUrl,
          actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform'],
        },
      },
    };

    this.addNodes(articleNode, webpageNode);
  }

  /**
   * Adds a CollectionPage node to the graph, ideal for the /news page.
   */
  public addCollectionPage(config: { name: string; description: string; mainEntity: Schema; searchPath?: string }) {
    // Added searchPath
    const origin = environment.canonicalOrigin;
    const pagePath = this.router.url.split('?')[0].split('#')[0];
    const pageUrl = new URL(pagePath, origin).href;

    const potentialAction: Schema | undefined = config.searchPath
      ? {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${new URL(config.searchPath, origin).href}?search={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        }
      : undefined;

    const collectionPageNode: Schema = {
      '@type': 'CollectionPage',
      '@id': pageUrl,
      url: pageUrl,
      name: `${config.name} | Summerry`,
      description: config.description,
      isPartOf: {
        '@id': `${origin}/#website`,
      },
      breadcrumb: {
        '@id': `${pageUrl}#breadcrumb`,
      },
      mainEntity: config.mainEntity,
      ...(potentialAction && { potentialAction }), // Add potentialAction if defined
    };

    this.addNodes(collectionPageNode);
  }

  /**
   * Adds a generic WebPage node to the graph.
   * Useful for static pages like About, Privacy Policy, Terms of Service, etc.
   * @param config Configuration for the WebPage.
   */
  public addWebPage(config: {
    name: string;
    description: string;
    breadcrumbItems: Array<{ name: string; item?: string }>;
    potentialAction?: Schema;
    mainEntity?: Schema;
    hasPart?: Schema[]; // Add hasPart here
  }) {
    const normalizedOrigin = new URL(environment.canonicalOrigin).origin;
    const pagePath = this.router.url.split('?')[0].split('#')[0];
    const pageUrl = new URL(pagePath, normalizedOrigin).href;

    const webpageNode: Schema = {
      '@type': 'WebPage',
      '@id': pageUrl,
      url: pageUrl,
      name: `${config.name} | Summerry`,
      description: config.description,
      isPartOf: {
        '@id': `${normalizedOrigin}/#website`, // Use normalizedOrigin
      },
      breadcrumb: {
        '@id': `${pageUrl}#breadcrumb`, // Corrected: Ensured no trailing slash if pageUrl might have one
      },
      potentialAction: config.potentialAction,
      mainEntity: config.mainEntity,
      hasPart: config.hasPart,
    };

    this.addBaseNodes(); // Always add base nodes
    this.addBreadcrumb(config.breadcrumbItems); // Add breadcrumbs
    this.addNodes(webpageNode); // Add the WebPage node itself
    this.generateAndInjectSchema(); // Generate and inject
  }
}
